import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ExternalLink, X } from 'lucide-react';
import {
  getBillingMe,
  type BillingSnapshot,
  type MeterId,
  type QuotaEntry,
} from '../api/billing';
import { useMe } from '../auth/MeContext';
import { useT } from '../I18nContext';
import type { Translator, StringKey } from '../i18n';

/**
 * Plan modal: read-only view of the active client's tier + usage.
 *
 * Opened from the sidebar's UserMenu. Mirrors the visual language of
 * SettingsModal / HelpModal - same scrim + header pattern.
 *
 * Why read-only here?
 *   The customer-facing dashboard hasn't grown CSRF plumbing yet, and
 *   the Stripe POST routes (/checkout/subscription, /portal, /cancel,
 *   /resume) require it. Rather than half-implement self-serve here,
 *   the modal exposes the same `manage in admin` link super-admin or
 *   client-admin sees, and points them at /admin/billing for the
 *   actual controls. Non-admin users get a "ask your admin" message.
 */
// Mapping from meter id → catalogue key. The translator resolves it
// at render time, so the labels track the active language.
const METER_LABEL_KEY: Record<MeterId, StringKey> = {
  documents_indexed: 'plan_modal.meter_documents_indexed',
  platform_users:    'plan_modal.meter_platform_users',
  widgets:           'plan_modal.meter_widgets',
};

interface PlanModalProps {
  onClose: () => void;
}

export function PlanModal({ onClose }: PlanModalProps) {
  const { me } = useMe();
  const t = useT();

  // Esc to close - same convention as SettingsModal.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const billingQuery = useQuery({
    queryKey: ['billing', 'me'],
    queryFn:  () => getBillingMe(),
    enabled:  !!me?.current_client,
  });

  const data = billingQuery.data;
  const canManage = !!me?.is_admin;

  return (
    <div
      className="history-modal-scrim"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={t('plan_modal.title')}
    >
      <div
        className="history-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="history-modal-header">
          <h2 className="history-modal-title">{t('plan_modal.title')}</h2>
          <button
            type="button"
            className="history-modal-close"
            onClick={onClose}
            aria-label={t('common.close')}
          >
            <X size={18} />
          </button>
        </header>

        <div className="history-modal-body">
          {billingQuery.isLoading && <p className="dim">{t('common.loading')}</p>}
          {billingQuery.isError && (
            <p role="alert">
              {t('plan_modal.error_load')}{' '}
              {billingQuery.error instanceof Error
                ? billingQuery.error.message
                : t('plan_modal.error_try_later')}
            </p>
          )}
          {data && (
            <>
              <PlanHeader data={data} t={t} />
              <UsageList data={data} t={t} />
              <ManageFooter canManage={canManage} t={t} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}


function PlanHeader({ data, t }: { data: BillingSnapshot; t: Translator }) {
  const tier = data.tier;
  // The full sentence is one catalogue value ('{price} / month'),
  // so translators control the spacing and the word order around
  // the price token, they aren't forced into "<price> / month".
  const price = tier.price_monthly_minor && tier.price_monthly_minor > 0
    ? t('plan_modal.price_per_month', { price: formatPrice(tier.price_monthly_minor, tier.currency) })
    : t('plan_modal.price_free');
  return (
    <section className="plan-modal-header">
      <div className="plan-modal-tier-name">
        {tier.display_name || titleCase(tier.id, t)}
      </div>
      <div className="plan-modal-tier-price">{price}</div>
      {tier.description && (
        <p className="plan-modal-tier-desc">{tier.description}</p>
      )}
      {data.subscription.cancel_at_period_end && data.subscription.current_period_end && (
        <p className="plan-modal-tier-status warn">
          {/* Catalogue value renders the date plain (no <strong>) so
              translators don't have to deal with markup. CSS on
              .plan-modal-tier-status.warn already provides emphasis. */}
          {t('plan_modal.cancels_on', { date: formatDate(data.subscription.current_period_end) })}
        </p>
      )}
      {data.subscription.status === 'past_due' && (
        <p className="plan-modal-tier-status danger">
          <strong>{t('plan_modal.payment_failed_strong')}</strong>{t('plan_modal.payment_failed_body')}
        </p>
      )}
    </section>
  );
}


function UsageList({ data, t }: { data: BillingSnapshot; t: Translator }) {
  const order: MeterId[] = ['documents_indexed', 'platform_users', 'widgets'];
  return (
    <section className="plan-modal-usage">
      <h3 className="plan-modal-section-title">{t('plan_modal.usage_heading')}</h3>
      {order.map((m) => (
        <UsageRow key={m} label={t(METER_LABEL_KEY[m])} entry={data.quota[m]} t={t} />
      ))}
    </section>
  );
}


function UsageRow({ label, entry, t }: { label: string; entry: QuotaEntry; t: Translator }) {
  const { used, limit, unlimited } = entry;
  const pct = !unlimited && limit && limit > 0
    ? Math.round((used / limit) * 100)
    : 0;
  return (
    <div className="plan-modal-usage-row">
      <div className="plan-modal-usage-head">
        <span className="plan-modal-usage-label">{label}</span>
        <span className="plan-modal-usage-counts">
          <strong>{used.toLocaleString()}</strong>
          {unlimited ? (
            <span className="plan-modal-usage-dim">{t('plan_modal.usage_unlimited')}</span>
          ) : (
            limit !== null && t('plan_modal.usage_limit_suffix', { limit: limit.toLocaleString() })
          )}
        </span>
      </div>
      {!unlimited && limit !== null && (
        <div className="plan-modal-usage-track">
          <div
            className={
              'plan-modal-usage-fill' +
              (pct >= 95 ? ' danger' : pct >= 80 ? ' warn' : '')
            }
            style={{ width: `${Math.min(100, pct)}%` }}
          />
        </div>
      )}
    </div>
  );
}


function ManageFooter({ canManage, t }: { canManage: boolean; t: Translator }) {
  if (canManage) {
    return (
      <section className="plan-modal-manage">
        <a
          className="plan-modal-manage-link"
          href="/admin/billing"
          target="_blank"
          rel="noopener noreferrer"
        >
          {t('plan_modal.manage_link')}
          <ExternalLink size={14} />
        </a>
        <p className="plan-modal-manage-hint">
          {t('plan_modal.admin_hint')}
        </p>
      </section>
    );
  }
  return (
    <section className="plan-modal-manage">
      <p className="plan-modal-manage-hint">
        {t('plan_modal.non_admin_hint')}
      </p>
    </section>
  );
}


// ── Helpers ────────────────────────────────────────────────────────

function formatPrice(minor: number, currency: string): string {
  const sym = currency.toUpperCase() === 'GBP' ? '£'
            : currency.toUpperCase() === 'USD' ? '$'
            : currency.toUpperCase() === 'EUR' ? '€'
            : '';
  return `${sym}${(minor / 100).toFixed(2)}`;
}

function formatDate(iso: string): string {
  try { return new Date(iso).toLocaleDateString(); }
  catch { return iso; }
}

function titleCase(s: string, t: Translator): string {
  if (!s) return t('plan_modal.tier_name_fallback');
  return s.charAt(0).toUpperCase() + s.slice(1).replace(/-/g, ' ');
}
